import type { I18nProvider } from "@refinedev/core";

import { appMessages } from "./messages.app";
import { serviceAccountMessages, animalTagMessages } from "./messages.generated";
import { benoem } from "../benaming";

// jve-5-1: the SPA i18n layer (FR14/FR17). Refine's I18nProvider over a flat, generated message
// registry ported from the Jmix bundles (see scripts/port-sa-i18n.mjs). Kept dependency-free — no
// i18next — matching the no-SaaS / lightweight ethos; the Epic-8.3 "one key source of truth" infra
// will later own extraction across all modules.

export const SUPPORTED_LOCALES = ["nl", "en"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
const DEFAULT_LOCALE: Locale = "en"; // the base bundle (messages_en): only the fallback for a key missing in the active bundle
const STORAGE_KEY = "fawkes.locale";

// Generated domain keys (from the Jmix bundles) + hand-authored SPA chrome keys. App keys win on
// collision (none today), so a deliberate override stays possible.
const REGISTRY: Record<Locale, Record<string, string>> = {
  nl: { ...serviceAccountMessages.nl, ...animalTagMessages.nl, ...appMessages.nl },
  en: { ...serviceAccountMessages.en, ...animalTagMessages.en, ...appMessages.en },
};

function isSupported(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Welke taal de applicatie toont (Maarten, 2026-10-13):
 * <ol>
 *   <li>wat de gebruiker zelf gekozen heeft, op het inlogscherm of bij Voorkeuren. Die keuze wordt bewaard en gaat
 *     vóór de systeemtaal; na het inloggen gaat hij ook mee met het account (zie TaalVanAccount);</li>
 *   <li>anders de taal van het systeem van de gebruiker, als de applicatie die kent: een nieuwe klant ziet meteen
 *     zijn eigen taal;</li>
 *   <li>anders Engels.</li>
 * </ol>
 */
export function resolveInitialLocale(): Locale {
  const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
  if (stored && isSupported(stored)) {
    return stored;
  }
  const candidates = globalThis.navigator?.languages ?? [globalThis.navigator?.language ?? ""];
  for (const tag of candidates) {
    const base = tag.toLowerCase().split("-")[0];
    if (isSupported(base)) {
      return base;
    }
  }
  return DEFAULT_LOCALE;
}

/** Of de gebruiker in deze browser zelf een taal gekozen heeft (en de systeemtaal dus niet meer telt). */
export function heeftEigenTaalkeuze(): boolean {
  const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
  return !!stored && isSupported(stored);
}

let currentLocale: Locale = resolveInitialLocale();

/**
 * De taal van de pagina. Onderdelen die niets van deze vertalingen weten lezen hem daar — het datumveld
 * van @fawkes/ui haalt er zijn maandnamen uit — en schermlezers gaan er ook op af.
 */
function zetPaginaTaal(lang: Locale) {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lang;
  }
}

zetPaginaTaal(currentLocale);

/**
 * Substitute Java-style positional placeholders (`%s`, `%d`) left-to-right from `args`. Most keys
 * carry none; the few that do (e.g. "%d IP range(s) removed") get their values from
 * `translate(key, { args: [n] })`. `%%` is an escaped literal percent.
 */
function interpolate(template: string, args?: unknown[]): string {
  if (!args || args.length === 0) {
    return template.replace(/%%/g, "%");
  }
  let i = 0;
  return template.replace(/%%|%[sd]/g, (token) => {
    if (token === "%%") return "%";
    return i < args.length ? String(args[i++]) : token;
  });
}

export const i18nProvider: I18nProvider = {
  translate: (key: string, options?: unknown, defaultMessage?: string): string => {
    const template = REGISTRY[currentLocale]?.[key] ?? REGISTRY[DEFAULT_LOCALE]?.[key];
    if (template === undefined) {
      return defaultMessage ?? key;
    }
    const args = (options as { args?: unknown[] } | undefined)?.args;
    // Pension of Hotel (Instellingen › Pension › Benaming): alleen het woord, nooit een sleutel.
    return benoem(interpolate(template, args));
  },
  getLocale: () => currentLocale,
  changeLocale: (lang: string) => {
    if (isSupported(lang)) {
      currentLocale = lang;
      globalThis.localStorage?.setItem(STORAGE_KEY, lang);
      zetPaginaTaal(lang);
    }
    return Promise.resolve();
  },
};
